import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import UnorderedListOutlined from '@ant-design/icons/UnorderedListOutlined';
import OrderedListOutlined from '@ant-design/icons/OrderedListOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import { RibbonIconButton } from '../bookDefaultStyles/WordStyleRibbon';
import { applyListFormatToText, getListFormatState, type ListFormatAction } from '../../utils/textListFormat';

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  textareaRef?: React.RefObject<TextAreaRef | null>;
  disabled?: boolean;
  className?: string;
  showIndentControls?: boolean;
};

function getNativeTextArea(textareaRef?: React.RefObject<TextAreaRef | null>) {
  const node = textareaRef?.current;
  if (!node) return null;
  return node.resizableTextArea?.textArea ?? null;
}

export const ListFormatToolbar: React.FC<Props> = ({
  value = '',
  onChange,
  textareaRef,
  disabled = false,
  className,
  showIndentControls = true,
}) => {
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  useEffect(() => {
    const el = getNativeTextArea(textareaRef);
    if (!el) {
      const len = String(value ?? '').length;
      setSelection({ start: 0, end: len });
      return;
    }

    const syncSelection = () => {
      setSelection({
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? el.selectionStart ?? 0,
      });
    };

    syncSelection();
    el.addEventListener('select', syncSelection);
    el.addEventListener('keyup', syncSelection);
    el.addEventListener('mouseup', syncSelection);
    el.addEventListener('focus', syncSelection);

    return () => {
      el.removeEventListener('select', syncSelection);
      el.removeEventListener('keyup', syncSelection);
      el.removeEventListener('mouseup', syncSelection);
      el.removeEventListener('focus', syncSelection);
    };
  }, [textareaRef, value]);

  const { bulletActive, numberActive } = useMemo(
    () => getListFormatState(value, selection.start, selection.end),
    [value, selection.start, selection.end],
  );

  const applyListAction = useCallback(
    (action: ListFormatAction) => {
      if (disabled || !onChange) return;

      const el = getNativeTextArea(textareaRef);
      const text = String(value ?? '');
      const start = el?.selectionStart ?? 0;
      const end = el ? (el.selectionEnd ?? start) : text.length;
      const result = applyListFormatToText(text, start, end, action);
      onChange(result.text);

      if (el) {
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(result.selectionStart, result.selectionEnd);
          setSelection({ start: result.selectionStart, end: result.selectionEnd });
        });
      } else {
        setSelection({ start: 0, end: result.text.length });
      }
    },
    [disabled, onChange, textareaRef, value],
  );

  return (
    <div
      className={['list-format-toolbar', className].filter(Boolean).join(' ')}
      role="toolbar"
      aria-label="List formatting"
    >
      <RibbonIconButton
        title="Bullet list"
        active={bulletActive}
        disabled={disabled}
        onClick={() => applyListAction('bullet')}
      >
        <UnorderedListOutlined />
      </RibbonIconButton>
      <RibbonIconButton
        title="Numbered list"
        active={numberActive}
        disabled={disabled}
        onClick={() => applyListAction('number')}
      >
        <OrderedListOutlined />
      </RibbonIconButton>
      {showIndentControls ? (
        <>
          <RibbonIconButton title="Increase list indent" disabled={disabled} onClick={() => applyListAction('indent')}>
            <MenuUnfoldOutlined />
          </RibbonIconButton>
          <RibbonIconButton title="Decrease list indent" disabled={disabled} onClick={() => applyListAction('outdent')}>
            <MenuFoldOutlined />
          </RibbonIconButton>
        </>
      ) : null}
    </div>
  );
};
